import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import { ReactComponent as AssetPattern } from '~/assets/svg/asset-1.svg';

interface FloatingElementProps {
  delay: number;
  size: number;
  x: string;
  y: string;
  duration: number;
}

const FloatingElement: React.FC<FloatingElementProps> = ({
  delay,
  size,
  x,
  y,
  duration,
}) => {
  return (
    <motion.div
      className="absolute opacity-10"
      style={{
        left: x,
        top: y,
        width: `${size}px`,
        height: `${size}px`,
      }}
      initial={{
        scale: 0.8,
        rotate: 0,
        opacity: 0.05,
      }}
      animate={{
        scale: [0.8, 1.2, 0.8],
        rotate: [0, 180, 360],
        opacity: [0.05, 0.15, 0.05],
        x: [0, 30, -20, 0],
        y: [0, -40, 20, 0],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      <AssetPattern className="h-full w-full text-current" />
    </motion.div>
  );
};

export const AnimatedBackground: React.FC = () => {
  // Generate pattern elements with random positions and sizes
  // Memoize to prevent regeneration on every render
  const elements = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        delay: Math.random() * 5,
        size: 40 + Math.random() * 80, // Size between 40-120px
        x: `${Math.random() * 100}%`,
        y: `${Math.random() * 100}%`,
        duration: 8 + Math.random() * 12, // Duration between 8-20 seconds
      })),
    [],
  );

  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden">
      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50/20 via-transparent to-orange-50/20" />

      {/* Animated pattern elements */}
      {elements.map((element) => (
        <FloatingElement
          key={element.id}
          delay={element.delay}
          size={element.size}
          x={element.x}
          y={element.y}
          duration={element.duration}
        />
      ))}

      {/* Additional larger elements for depth */}
      <motion.div
        className="absolute left-10 top-10 opacity-5"
        animate={{
          rotate: [0, 360],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        <AssetPattern className="h-32 w-32 text-purple-400" />
      </motion.div>

      <motion.div
        className="absolute bottom-20 right-20 opacity-5"
        animate={{
          rotate: [360, 0],
          scale: [1, 0.9, 1],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        <AssetPattern className="h-40 w-40 text-orange-400" />
      </motion.div>

      <motion.div
        className="absolute left-1/4 top-1/2 opacity-5"
        animate={{
          x: [0, 50, -30, 0],
          y: [0, -30, 40, 0],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <AssetPattern className="h-24 w-24 text-purple-300" />
      </motion.div>
    </div>
  );
};
